import { AriaTableProps, useTable } from '@react-aria/table';
import { mergeProps } from '@react-aria/utils';
import { TableStateProps, useTableState } from '@react-stately/table';
import { SelectionBehavior, SelectionMode } from '@react-types/shared';
import React, { PropsWithoutRef, RefAttributes, useMemo } from 'react';

import { VariantProps } from 'class-variance-authority';
import {
    TableBody as TableBodyBase,
    TableCell as TableCellBase,
    TableColumn as TableColumnBase,
    TableHeader as TableHeaderBase,
    TableRow as TableRowBase
} from './base';
import LoadingBar from '../LoadingBar';
import TableBody from './Body';
import { default as TableColumnHeader } from './ColumnHeader';
import { default as TableHeaderRow } from './HeaderRow';

import { GridNode } from '@react-types/grid';
import TableContext, { TableConfig } from './Context';
import { useDOMRef } from './hooks/useDOMRef';
import useIsInfinityScroll from './hooks/useIsInfinityScroll';
import TableRowGroup from './RowGroup';
import TableSelectAllCheckbox from './SelectAllCheckbox';
import { tableClassNames, tableContainerClassNames } from './styles';

export type SortDescriptor<T = unknown> = TableStateProps<T>['sortDescriptor'] & {};

interface Props<T> extends TableStateProps<T> {
    selectionMode?: SelectionMode;
    selectionBehavior?: SelectionBehavior;
    hideLoading?: boolean;
    sortDescriptor?: SortDescriptor<T>;
    containerClassName?: string;
    rowClassName?: (item: any) => string | undefined;

    /** @deprecated */
    compact?: boolean;
    /** @deprecated */
    sticked?: boolean;
    /** @deprecated */
    bordered?: boolean;
    /** @deprecated */
    borderWeight?: any;
}

type NativeAttrs = Omit<React.TableHTMLAttributes<unknown>, keyof Props<object>>;

export type TableProps<T = object> = Props<T> &
    NativeAttrs &
    Omit<VariantProps<typeof tableClassNames>, 'isMultiple'> &
    VariantProps<typeof tableContainerClassNames> &
    Pick<AriaTableProps, 'onRowAction' | 'onCellAction'>;

const Table = React.forwardRef<HTMLTableElement, TableProps>((tableProps, ref: React.Ref<HTMLTableElement | null>) => {
    const {
        selectionMode,
        selectionBehavior,
        containerClassName,
        hideLoading,
        children,
        color,
        borderWeight,
        bordered,
        className,
        rowClassName,
        ...otherProps
    } = tableProps;

    const state = useTableState({
        ...tableProps,
        children,
        showSelectionCheckboxes:
            tableProps.showSelectionCheckboxes !== undefined
                ? tableProps.showSelectionCheckboxes
                : selectionMode === 'multiple' && selectionBehavior !== 'replace'
    });

    const domRef = useDOMRef<HTMLTableElement>(ref);

    const { collection } = state;
    const isInfinityScroll = useIsInfinityScroll(collection);
    const {
        gridProps
    }: {
        gridProps: Omit<React.HTMLAttributes<unknown>, keyof TableProps<unknown>>;
    } = useTable(tableProps, state, domRef);

    const initialValues = React.useMemo<Partial<TableConfig>>(() => {
        return {
            collection,
            color,
            rowClassName
        };
    }, [collection, color, rowClassName]);

    const isHoverable = useMemo(() => !!selectionMode && selectionMode !== 'none', [selectionMode]);
    const isLoading =
        collection.body?.props?.loadingState === 'loading' || collection.body?.props?.loadingState === 'loadingMore';

    return (
        <TableContext.Provider defaultValues={initialValues}>
            <div className={tableContainerClassNames({ className: containerClassName })}>
                <table
                    ref={domRef}
                    className={tableClassNames({
                        className,
                        hoverable: isHoverable,
                        isMultiple: selectionMode === 'multiple'
                    })}
                    {...mergeProps(gridProps, otherProps)}
                >
                    <TableRowGroup as="thead" isFixed={isInfinityScroll}>
                        {collection.headerRows.map((headerRow) => (
                            <TableHeaderRow key={headerRow?.key} item={headerRow as GridNode<unknown>} state={state}>
                                {Array.from(headerRow.childNodes).map((column) =>
                                    column?.props?.isSelectionCell ? (
                                        <TableSelectAllCheckbox
                                            key={column?.key}
                                            column={column as GridNode<unknown>}
                                            state={state}
                                        />
                                    ) : (
                                        <TableColumnHeader
                                            key={column?.key}
                                            column={column as GridNode<unknown>}
                                            state={state}
                                        />
                                    )
                                )}
                            </TableHeaderRow>
                        ))}
                    </TableRowGroup>
                    <tbody className="tw-table tw-w-full tw-table-fixed" role="rowgroup">
                        <tr className="tw-w-full">
                            <td className="tw-block tw-w-full tw-p-0" colSpan={collection.columnCount}>
                                <LoadingBar isLoading={isLoading} className="tw-bg-accents-1" />
                            </td>
                        </tr>
                    </tbody>
                    <TableBody
                        collection={collection}
                        color={color}
                        hideLoading={hideLoading}
                        isStatic={!isHoverable}
                        isHoverable={isHoverable}
                        state={state}
                    />
                </table>
            </div>
        </TableContext.Provider>
    );
});

type TableComponent<T, P = {}> = React.ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>> & {
    Cell: typeof TableCellBase;
    Column: typeof TableColumnBase;
    Row: typeof TableRowBase;
    Header: typeof TableHeaderBase;
    Body: typeof TableBodyBase;
};

Table.displayName = 'Table';

export default Table as TableComponent<HTMLTableElement, TableProps>;
